import React, { useEffect, useState } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { adminListPackages, adminUpdatePackage } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Save, Star, Check } from 'lucide-react';

const AdminPackages = () => {
  const { lang } = useLang();
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setPkgs(await adminListPackages() || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (code, field, value) => setEdits((e) => ({ ...e, [code]: { ...(e[code] || {}), [field]: value } }));

  const save = async (p) => {
    const changes = edits[p.code];
    if (!changes || Object.keys(changes).length === 0) return;
    setSaving(p.code);
    try {
      await adminUpdatePackage(p.code, changes);
      toast({ title: lang === 'ar' ? 'تم الحفظ' : 'Saved' });
      setEdits((e) => { const c = { ...e }; delete c[p.code]; return c; });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'الباقات' : 'Packages'}</h1>
        <p className="text-sm text-[#111827]/60 mt-2">{lang === 'ar' ? 'تعديل أسعار الباقات وترتيبها' : 'Adjust package pricing and ordering'}</p>
      </div>

      {loading ? <div className="glass-card rounded-2xl p-10 text-center text-[#111827]/60">…</div>
      : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {pkgs.map((p) => {
            const changed = !!edits[p.code];
            const price = edits[p.code]?.price ?? p.price;
            const visas = edits[p.code]?.visas ?? p.visas;
            const popular = edits[p.code]?.popular ?? p.popular;
            return (
              <div key={p.code} className="glass-card rounded-2xl p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="font-display text-2xl text-[#0a0a0a]">{p.name?.en}</div>
                    <div className="text-xs text-[#2563eb] mt-1">{p.tagline?.en}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={popular} onCheckedChange={(v) => set(p.code, 'popular', v)} className="data-[state=checked]:bg-[#2563eb]" />
                    {popular && <Star className="w-4 h-4 text-[#3b82f6] fill-[#3b82f6]" />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#2563eb]/70 block mb-1">{lang === 'ar' ? 'السعر (AED)' : 'Price (AED)'}</label>
                    <Input type="number" value={price} onChange={(e) => set(p.code, 'price', parseInt(e.target.value, 10) || 0)} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#2563eb]/70 block mb-1">{lang === 'ar' ? 'تأشيرات' : 'Visas'}</label>
                    <Input type="number" value={visas} onChange={(e) => set(p.code, 'visas', parseInt(e.target.value, 10) || 0)} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
                  </div>
                </div>
                <div className="pt-3 border-t border-[#2563eb]/10">
                  <div className="text-[10px] uppercase tracking-widest text-[#2563eb]/70 mb-2">{lang === 'ar' ? 'يشمل' : 'Includes'}</div>
                  <ul className="space-y-1.5">
                    {p.features?.en?.slice(0, 6).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-[#111827]/70">
                        <Check className="w-3 h-3 text-[#2563eb] mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button disabled={!changed || saving === p.code} onClick={() => save(p)} className="gold-button w-full rounded-md h-9 text-xs mt-4">
                  <Save className="w-3.5 h-3.5 me-2" />{saving === p.code ? '…' : (lang === 'ar' ? 'حفظ' : 'Save')}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
